import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export default function TableLayout({
  columns,
  visibleColumns = [],
  rows,
  loading,
  onEdit,
  onDelete,
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleOpenDialog = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedId(null);
    setOpenDialog(false);
  };

  const handleConfirmDelete = () => {
    if (selectedId && onDelete) onDelete(selectedId);
    handleCloseDialog();
  };

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const showActions =
    typeof onEdit === "function" || typeof onDelete === "function";

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "60vh",
            height: "60vh",
            overflowY: "auto",

            /* Scrollbar styling */
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },

            /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor: "#c1c1c1 #f1f1f1",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    background: "#f0f2f5",
                    color: "#333",
                    borderBottom: "2px solid #e0e0e0",
                    paddingTop: "14px",
                    paddingBottom: "14px",
                  },
                }}
              >
                <TableCell width={70}>No.</TableCell>

                {columns
                  .filter((col) => visibleColumns.includes(col.key))
                  .map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}

                {showActions && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      1 +
                      columns.filter((col) => visibleColumns.includes(col.key))
                        .length +
                      (showActions ? 1 : 0)
                    }
                    align="center"
                    sx={{ height: "300px" }}
                  >
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : paginatedRows.length > 0 ? (
                paginatedRows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:hover": { backgroundColor: "#f7faff" },
                      transition: "0.2s ease",
                      "& td": {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        borderBottom: "1px solid #eee",
                      },
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    {columns
                      .filter((col) => visibleColumns.includes(col.key))
                      .map((col) => (
                        <TableCell key={col.key}>{row[col.key]}</TableCell>
                      ))}

                    {showActions && (
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {row.id !== 1 && (
                          <>
                            {onEdit && (
                              <IconButton
                                onClick={() => onEdit(row.id)}
                                color="primary"
                                size="small"
                                sx={{
                                  mx: 0.5,
                                  background: "#e8f0fe",
                                  "&:hover": { background: "#dbe6fc" },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {onDelete && (
                              <IconButton
                                onClick={() => handleOpenDialog(row.id)}
                                color="error"
                                size="small"
                                sx={{
                                  mx: 0.5,
                                  background: "#fdecea",
                                  "&:hover": { background: "#fbd2cf" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={
                      1 +
                      columns.filter((col) => visibleColumns.includes(col.key))
                        .length +
                      (showActions ? 1 : 0)
                    }
                    align="center"
                    sx={{ height: "300px", color: "#777", fontSize: "15px" }}
                  >
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ background: "#fafafa" }}
        />
      </Paper>

      {/* Delete dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
